import React, { useState } from 'react';
import { CreditCard, X, AlertTriangle, Mail, Plus, Zap } from 'lucide-react';
import { usePermissions } from '../contexts/PermissionsContext';

const OutOfCreditsModal = ({ isOpen, onClose, onViewCredits, currentUser, onUpdateCredits, canUseBoost = true }) => {
  const { isAdmin } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  if (!isOpen) return null;

  const handleQuickTopUp = async (amount) => {
    if (!onUpdateCredits || loading) return;
    
    setLoading(true);
    try {
      await onUpdateCredits({
        action: 'topup',
        amount: amount,
        userId: currentUser?.id
      });
      onClose();
    } catch (error) {
      console.error('Failed to top up credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomTopUp = async () => {
    const amount = parseInt(customAmount);
    if (!amount || amount <= 0) return;
    
    await handleQuickTopUp(amount);
    setCustomAmount('');
    setShowCustomInput(false);
  };

  const handleBoostCredits = async () => {
    await handleQuickTopUp(10000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Out of Credits
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-red-600" />
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Credits Remaining
          </h3>
          
          <p className="text-gray-600 mb-6">
            You have used all your credits for this month. Please contact the administrator to get more credits or increase your monthly limit.
          </p>
          
          <div className="space-y-3">
            {/* Admin Controls */}
            {isAdmin && onUpdateCredits && (
              <div className="border-b pb-4 mb-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-orange-500" />
                  Admin Quick Actions
                </h4>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    onClick={() => handleQuickTopUp(1000000)}
                    className="px-3 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                    disabled={loading}
                  >
                    +1M Credits
                  </button>
                  <button
                    onClick={() => handleQuickTopUp(5000000)}
                    className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                    disabled={loading}
                  >
                    +5M Credits
                  </button>
                </div>
                
                {!showCustomInput ? (
                  <button
                    onClick={() => setShowCustomInput(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm flex items-center justify-center gap-2"
                    disabled={loading}
                  >
                    <Plus className="w-4 h-4" />
                    Custom Amount
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter amount"
                      disabled={loading}
                    />
                    <button
                      onClick={handleCustomTopUp}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      disabled={loading || !customAmount}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowCustomInput(false);
                        setCustomAmount('');
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      disabled={loading}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* User Boost Option */}
            {!isAdmin && canUseBoost && onUpdateCredits && (
              <div className="border-b pb-4 mb-4">
                <button
                  onClick={handleBoostCredits}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all"
                  disabled={loading}
                >
                  <Zap className="w-4 h-4" />
                  {loading ? 'Adding Credits...' : 'Get 10K Bonus Credits (One-time)'}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  One-time bonus credits for this month only
                </p>
              </div>
            )}
            
            <button
              onClick={onViewCredits}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <CreditCard className="w-4 h-4" />
              View Credit Details
            </button>
            
            <button
              onClick={() => {
                window.location.href = 'mailto:admin@supple.tools?subject=Credit%20Request&body=Hello,%20I%20need%20more%20credits%20for%20my%20account.';
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Mail className="w-4 h-4" />
              Contact Administrator
            </button>
          </div>
        </div>
        
        <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <span className="font-medium">Note:</span> Credits reset monthly. You'll receive your full allowance at the beginning of next month.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OutOfCreditsModal;